import type { ReactNode } from 'react';
import { classes } from '@src/components/layout/space';
import { testIdAttr } from '@src/components/testId';
import './Container.css';

export type ContainerWidth =
  | 'medium'
  | 'full'
  | 'measure'
  | 'content'
  | 'wide'
  | 'narrow'
  | 'auth'
  | 'dialog';
export type ContainerGutter = 'none' | 'default' | 'narrow';
export type ContainerPad = 'none' | 'header' | 'page' | 'main' | 'box' | 'compact';

export interface ContainerProps {
  children?: ReactNode;
  /**
   * medium — quick draw, settings, creation, box select.
   * full — standard page column; prefer PageContent for screens.
   * measure — alias for medium (nested lead copy).
   * content — alias for full.
   * wide — legacy 90rem; do not use for new pages.
   * narrow — one mobile-width column.
   * auth — the sign-in card.
   * dialog — save-access and modal-width forms.
   */
  width?: ContainerWidth;
  /**
   * Horizontal inset. `default` shrinks to `narrow` below 48rem — the only
   * place that breakpoint is allowed to live.
   */
  gutter?: ContainerGutter;
  pad?: ContainerPad;
  /** Fill leftover height inside the shell stage. */
  grow?: boolean;
  as?: 'div' | 'main' | 'section' | 'header';
  className?: string;
  testId?: string;
}

export function Container({
  children,
  width = 'content',
  gutter = 'none',
  pad = 'none',
  grow = false,
  as: Tag = 'div',
  className,
  testId,
}: ContainerProps) {
  return (
    <Tag
      className={classes(
        'container',
        `container--${width}`,
        gutter !== 'none' && `container--gutter-${gutter}`,
        pad !== 'none' && `container--pad-${pad}`,
        grow && 'container--grow',
        className,
      )}
      {...testIdAttr(testId)}
    >
      {children}
    </Tag>
  );
}
